import React, { useEffect, useReducer, Suspense } from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { RegistrationButton } from "./RegistrationButton";
import { useTranslation } from "react-i18next";
import ReactHtmlParser from "html-react-parser";
import { useNavigate } from "react-router-dom";
import "./registration.css";

export const RegStep1 = ({ globalEvent, setGlobalEvent }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Initialize social login buttons
  useEffect(() => {
    let cleared = false;
    let initialized = { google: false, apple: false };

    const ready = {
      google: !!window.google?.accounts?.id,
      apple: !!window.AppleID,
    };

    const maybeInit = () => {
      if (!cleared && ready.apple && !initialized.apple) {
        try {
          window.AppleID.auth.init({
            clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
            scope: "name email",
            redirectURI: process.env.REACT_APP_CLIENT_URL,
            state: "SignInUserAuthenticationRequest",
            nonce: String(Date.now()),
            usePopup: true,
          });
          initialized.apple = true;
        } catch (error) {
          console.error("Apple initialization failed:", error);
        }
      }
      
      if (!cleared && ready.google && !initialized.google) {
        // Google is already initialized in App.js, just render the button
        const btn = document.getElementById("googleButtonDiv");
        if (btn && !btn.hasChildNodes()) { // Only render if button is empty
          try {
            window.google.accounts.id.renderButton(btn, {
              theme: "outline",
              size: "large",
              shape: "circle",
              width: window.innerWidth <= 900 ? "310" : "375",
              logo_alignment: "center",
              text: "signup_with",
            });
            initialized.google = true;
          } catch (error) {
            console.error("Google button render failed:", error);
          }
        }
      }
    };

    // Try immediately, then poll briefly as a fallback
    maybeInit();
    const int = setInterval(() => {
      const previousGoogle = ready.google;
      const previousApple = ready.apple;
      
      ready.google ||= !!window.google?.accounts?.id;
      ready.apple  ||= !!window.AppleID;
      
      // Only call maybeInit if something new became ready
      if ((ready.google && !previousGoogle) || (ready.apple && !previousApple)) {
        maybeInit();
      }
      
      // Stop polling when both are initialized
      if (initialized.google && initialized.apple) {
        clearInterval(int);
      }
    }, 100);

    return () => { 
      cleared = true; 
      clearInterval(int); 
    };
  }, []); // run once

  useEffect(() => {

    const appleBtn = document.getElementById("appleid-signin");
    if (appleBtn) {
      const handleAppleClick = () => {
        window.dataLayer.push({
          event: 'cta_click',
          button_name: 'add_user_data_step_1_apple',
          page_path: window.location.pathname
        });
      };
  
      appleBtn.addEventListener("click", handleAppleClick);
  
      return () => {
        appleBtn.removeEventListener("click", handleAppleClick);
      };
    }
  }, []);
  

  const showRegStep2 = (event, data) => {
    event.stopPropagation();
    
    window.dataLayer.push({
      event: 'cta_click',
      button_name: 'add_user_data_step_1_email',
      page_path: window.location.pathname
    });

    setGlobalEvent({ type: "showRegStep2", data: data });
  };

  return (
    <Box
      sx={{
        mt: { sx: 0, sm: 16 },
        mb: 5,
        px: { xs: 2, lg: 0 },
      }}
    >
      <Box>
        <Typography
          variant="h6"
          sx={{
            mb: 4,
          }}
        >
          {ReactHtmlParser(t("registration.homeTitle"))}
        </Typography>
        <Typography
          variant="body3"
          sx={{
            color: "rgba(43, 54, 116, .7)",
          }}
        >
          {ReactHtmlParser(t("registration.text"))}
        </Typography>
      </Box>

      <Stack
        direction={"column"}
        spacing={1}
        sx={{
          mt: { xs: 4, sm: 4 },
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <div style={{ width: "100%" }}>
          <div
            style={{ width: "100%" }}
            id="googleButtonDiv"
            className="google-btn"
            data-context="signup"
            data-ux_mode="popup"
            data-auto_prompt="false"
          ></div>
        </div>
        <div style={{ width: "100%" }}>
          <div
            style={{ width: "100%" }}
            id="appleid-signin"
            className="signin-button apple-btn"
            data-color="black"
            data-border="true"
            data-type="sign-up"
            data-border-radius="50"
            data-height="44"
          ></div>
        </div>

        <RegistrationButton
          bColor="secondary-blue-btn"
          onClick={(e) => {
            showRegStep2(e, {});
          }}
          text={t("registration.buttonregwithemail")}
        />
      </Stack>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Stack
          direction={"row"}
          sx={{
            mt: 4,
            alignItems: "center",
            justifyContent: "center",
            width: "310px",
          }}
        >
          <Typography
            variant="body2"
            sx={{ lineHeight: "20.16px", fontWeight: 500 }}
          >
            {ReactHtmlParser(t("common.alreadyauser"))}
          </Typography>
          <Button
            className="secondary-blue-text-btn"
            onClick={(e) => {
              navigate("/login");
            }}
            sx={{
              lineHeight: "20.16px",
              fontWeight: 700,
            }}
          >
            {ReactHtmlParser(t("common.dologin"))}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
